import { ApolloError, useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';

import {
  getApolloErrorCodes,
  isPinCodeError,
  isUnauthorizedError,
} from '../../GraphQL/api/graphql-errors';
import {
  ConversationChatMessageItemDataFragment,
  GetConversationChatMessageItemsDocument,
  GetConversationChatMessageItemsQueryVariables,
} from '../../types/api/graphql';
import { useConversationMessageItemFormatter } from './use-conversation-message-item-formatter';

const knownBackendErrorMessages = ['Unexpected Execution Error'];

export function useConversationChatMessageItems() {
  const abortController = useRef(new AbortController());
  const [onLoadConversationChatMessagesData] = useLazyQuery(
    GetConversationChatMessageItemsDocument,
    { fetchPolicy: 'network-only' }
  );

  const { formatter } = useConversationMessageItemFormatter();

  // Abort the in-flight request when this hook unmounts. ChatContainer is keyed
  // by conversation id, so unmount === "the user switched to another
  // conversation" — we cancel the old request immediately instead of letting it
  // run to completion (or block the new one) in the background.
  useEffect(() => {
    const controller = abortController.current;
    return () => controller.abort();
  }, []);

  const onLoadMessageItems = useCallback(
    async (variables: GetConversationChatMessageItemsQueryVariables) => {
      const { signal } = abortController.current;

      try {
        const result = await onLoadConversationChatMessagesData({
          variables,
          context: { fetchOptions: { signal } },
        });

        // The request was superseded — the user switched away and we aborted
        // it on unmount. Apollo *resolves* (rather than throws) an aborted lazy
        // query, so we must check here too, not only in catch. Ignore it so we
        // never flash "Fout bij ophalen berichten" on the new conversation.
        if (signal.aborted) {
          return undefined;
        }

        // Pincode (428) / auth (401) failures are handled globally (lock /
        // re-login). Return `undefined` so the UI ignores them rather than
        // flashing "Fout bij ophalen berichten".
        const codes = getApolloErrorCodes(result.error);
        if (isPinCodeError(codes) || isUnauthorizedError(codes)) {
          return undefined;
        }

        if (
          result.error?.message &&
          knownBackendErrorMessages.includes(result.error.message)
        ) {
          return null;
        }

        if (result.data?.messages) {
          return {
            ...result.data.messages,
            items: (result.data.messages.items ?? [])
              .filter(option => option)
              .map(option =>
                formatter(option as ConversationChatMessageItemDataFragment)
              ),
          };
        }

        return null;
      } catch (error) {
        // A request we deliberately aborted (the user switched conversation) is
        // not a load failure — tell the caller to ignore it.
        if (signal.aborted) {
          return undefined;
        }
        // Same for pincode/auth failures thrown as network errors.
        if (error instanceof ApolloError) {
          const codes = getApolloErrorCodes(error);
          if (isPinCodeError(codes) || isUnauthorizedError(codes)) {
            return undefined;
          }
        }
        return null;
      }
    },
    [onLoadConversationChatMessagesData, formatter]
  );

  return {
    onLoad: onLoadMessageItems,
  };
}
